import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ImgComparisonSlider,
  type HTMLImgComparisonSliderElement,
} from '@img-comparison-slider/react'
import 'img-comparison-slider/dist/styles.css'
import { useConfigStore } from '@renderer/stores/configState'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { usePrintStore } from '@renderer/stores/printStore'
import { initiatePrint } from '@renderer/utils/pipeline/printService'
import { Button, LoadableImage } from '@renderer/components'
import AiStageShell from './AiStageShell'
import { BackgroundContainer, GlassPanel } from '@renderer/components/BackgroundContainer'
import { useDesignLayout, useSessionLayout } from '@renderer/hooks'
import { fetchRemoteImageAsBlobUrl } from '@renderer/utils/galleryUrlUtils'
import { getCachedImageUrl } from '@renderer/utils/imageCacheUtils'
import type { PostSessionAiTier } from '@renderer/types/configState'
import type { CapturedItem } from '@renderer/types/sessionState'
import { resolvePostSessionAiBooth } from '@renderer/utils/sanitizePostSessionAiBooth'
import PostSessionAiPaymentModal, { type AiEffectPaidInfo } from './PostSessionAiPaymentModal'
import { isDigitalMode } from '@renderer/lib/runtime'
import { useResolvedProjectId } from '@renderer/hooks/useResolvedProjectId'

type PreviewFetchStatus = 'idle' | 'loading' | 'error' | 'done'

/** Strip `mirror:` prefix used by some flows; cache/fetch uses the underlying URL. */
function displaySourceUrl(photoUrl: string): string {
  return photoUrl.startsWith('mirror:') ? photoUrl.slice(7) : photoUrl
}

function isEligibleRawPhotoUrl(url: string | undefined): boolean {
  if (!url) return false
  const u = displaySourceUrl(url)
  return (
    u.startsWith('https') ||
    u.startsWith('http') ||
    u.startsWith('blob:') ||
    u.startsWith('data:') ||
    u.startsWith('/')
  )
}

function collectRawPhotoItems(items: CapturedItem[]): CapturedItem[] {
  return items.filter(
    (item) =>
      isEligibleRawPhotoUrl(item.photoUrl) &&
      !item.id?.includes('dreambooth-strip') &&
      !item.id?.includes('dreambooth-gif')
  )
}

function normalizeTier(raw: unknown): PostSessionAiTier {
  return raw === 'fast' || raw === 'quality' || raw === 'studio' ? raw : 'quality'
}

/**
 * Hard cap on a single AI generation ATTEMPT so a hung backend can't strand the guest on a
 * spinner forever. On timeout the request is aborted and the user regains control
 * (Skip/Continue) with a friendly message instead of an indefinite wait.
 *
 * MUST stay above the server's own `maxDuration` (120s on /api/ai/post-session). Aborting
 * early does NOT cancel the server: it finishes the generation and keeps the credit debit,
 * so the owner pays for an image the booth threw away. At 45s this fired routinely — a
 * Vertex image generation is 15–45s before upload — and was a direct cause of the
 * "AI failed but credits were still deducted" reports. Let the server win the race so its
 * own refund-on-failure path can run and the booth sees a real error response.
 */
const AI_GENERATION_TIMEOUT_MS = 130000
/** A blob-only strip may still be uploading server-side; retry a 409 a couple of times. */
const STRIP_UPLOAD_RETRY_MAX = 2
const STRIP_UPLOAD_RETRY_DELAY_MS = 1500

/**
 * Mirror of the server credit pricing (config/creditPricing.ts) so the booth can decide
 * whether to charge the guest. The guest only pays when their cheapest package exceeds the
 * AI credit cost; otherwise the owner's credits fund it (and we skip the payment prompt).
 * 1 credit = Rp 1,000.
 */
const AI_CREDIT_PRICE_IDR = 1000
const AI_TIER_CREDITS: Record<PostSessionAiTier, number> = { fast: 2, quality: 4, studio: 6 }

/**
 * Select-stage preview: the customer's chosen photo as the "before", with an animated "mystery"
 * after panel (frosted shimmer + sparkle + "?") instead of the dashboard example — personal and
 * builds anticipation before the one-shot generation. Mirrors the real compare slider's framing
 * (Before/After chips + themed divider) without being draggable, since there is no result yet.
 */
const MysteryAfter: React.FC<{
  beforeUrl?: string
  themePrimary: string
  beforeLabel: string
  afterLabel: string
  /** Raw photos use 'cover' with a right-side mystery split; tall strips use 'contain' so the
   * whole strip stays visible, with a centered sparkle teaser instead of a left/right split. */
  objectFit?: 'cover' | 'contain'
}> = ({ beforeUrl, themePrimary, beforeLabel, afterLabel, objectFit = 'cover' }) => {
  const isContain = objectFit === 'contain'
  return (
    <div className="relative h-full w-full overflow-hidden">
      <LoadableImage
        src={beforeUrl}
        alt={beforeLabel}
        className={`absolute inset-0 h-full w-full ${isContain ? 'object-contain' : 'object-cover'}`}
      />
      {isContain ? (
        // Tall strip: don't split left/right — float a small sparkle teaser so the full strip shows.
        <div className="pointer-events-none absolute right-2 bottom-2 flex items-center gap-1">
          <span className="text-xl animate-pulse" aria-hidden>
            ✨
          </span>
          <span className="text-2xl font-extrabold" style={{ color: themePrimary }} aria-hidden>
            ?
          </span>
        </div>
      ) : (
        <div
          className="absolute inset-y-0 right-0 w-[46%] overflow-hidden border-l-[3px]"
          style={{ borderColor: themePrimary }}
        >
          <div
            className="absolute inset-0 backdrop-blur-md"
            style={{ background: `linear-gradient(135deg, ${themePrimary}33, ${themePrimary}11)` }}
          />
          <div
            className="absolute inset-0 animate-ai-shimmer"
            style={{
              backgroundImage:
                'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.5) 50%, transparent 65%)',
              backgroundSize: '250% 100%',
            }}
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
            <span className="text-2xl animate-pulse" aria-hidden>
              ✨
            </span>
            <span className="text-3xl font-extrabold" style={{ color: themePrimary }} aria-hidden>
              ?
            </span>
          </div>
        </div>
      )}
      <span className="pointer-events-none absolute left-2 top-2 z-10 rounded bg-black/45 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm">
        {beforeLabel}
      </span>
      <span
        className="pointer-events-none absolute right-2 top-2 z-10 rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm"
        style={{ backgroundColor: themePrimary }}
      >
        {afterLabel}
      </span>
    </div>
  )
}

const PostSessionAIPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  // In digital mode the route param is a slug; API calls must use the resolved
  // Mongo id (config._id) or the studio casts "panoramic" -> ObjectId -> 500.
  // Kiosk: resolves to the same id as the param (no-op). Nav still uses the param.
  const resolvedProjectId = useResolvedProjectId()
  const { config: globalConfig } = useConfigStore()
  const themePrimary = globalConfig?.theme?.primaryColor ?? '#3B82F6'
  const postAi = useMemo(
    () => resolvePostSessionAiBooth(globalConfig?.pages?.result?.settings?.postSessionAi),
    [globalConfig?.pages?.result?.settings?.postSessionAi]
  )
  /**
   * Customer-facing intro copy. Dashboard-configured `introTitle`/`introDescription` win; otherwise
   * fall back to the bundled i18n defaults so existing projects (no config) still read sensibly.
   * Used by the intro hero, the select-stage right panel, and the payment modal.
   */
  const introTitle = postAi?.introTitle || t('result.postSessionAi.introHeadline')
  const introDescription = postAi?.introDescription || t('result.postSessionAi.introBody')
  const { gallery, id: sessionId, transaction, userPreferences, setSessionState, uploadSessionState, logActivity } =
    useSessionStateStore()
  /** Guards the optional one-time AI-strip print so rapid re-renders/taps can't duplicate it. */
  const aiStripPrintedRef = useRef(false)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Measured aspect (w/h) of the actual BEFORE image. The raw/individual-photo effect
   *  isn't tied to a frame slot, so `framePhotoRatio` may be wrong or absent — the real
   *  photo's own dimensions are the ground truth (and the AI output was cropped to match
   *  them). Null until the image decodes; the box uses framePhotoRatio/3:4 until then. */
  const [rawImgRatio, setRawImgRatio] = useState<number | null>(null)
  /** Non-error transient status (e.g. waiting on a still-uploading strip). */
  const [statusHint, setStatusHint] = useState<string | null>(null)
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator === 'undefined' ? true : navigator.onLine
  )

  // Track connectivity so the AI step can show an offline hint and avoid a doomed request
  // (AI generation is online-only). Pairs with the dashboard/free-credits skip on the way in.
  useEffect(() => {
    const on = (): void => setIsOnline(true)
    const off = (): void => setIsOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return (): void => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])
  const [activePhotoId, setActivePhotoId] = useState<string | null>(null)
  const [filmstripSrcById, setFilmstripSrcById] = useState<Record<string, string>>({})
  /** Strip-only flows can't be detected via `activeItem.aiProcessedPhotoUrl` (no raw active item),
   * so we track local "have we applied at least once" to flip the action button to Continue. */
  const [stripApplied, setStripApplied] = useState(false)
  /**
   * Lead with "what this effect does" before asking the guest to pick a photo. While the intro is
   * showing we present the dashboard before/after example full-bleed with a short explanation; the
   * photo picker is only revealed once the guest taps "Try it on my photos". Skipped automatically
   * when there's no example to show (see `showIntro`).
   */
  const [introAcknowledged, setIntroAcknowledged] = useState(false)

  const configuredTier = useMemo(() => normalizeTier(postAi?.tier), [postAi?.tier])

  const [previewStatus, setPreviewStatus] = useState<PreviewFetchStatus>('idle')
  const [previewBeforeUrl, setPreviewBeforeUrl] = useState<string | undefined>(
    postAi?.previewBeforeUrl
  )
  const [previewAfterUrl, setPreviewAfterUrl] = useState<string | undefined>(
    postAi?.previewAfterUrl
  )

  const previewSliderBlobsRef = useRef<{ before: string; after: string } | null>(null)
  const [previewSliderBlobs, setPreviewSliderBlobs] = useState<{
    before: string
    after: string
  } | null>(null)
  const [previewSliderBlobError, setPreviewSliderBlobError] = useState(false)

  /** Slider `value` (0–100): higher ⇒ more of slot `first` (before) visible. Drives label visibility. */
  const [compareDividerPct, setCompareDividerPct] = useState(50)
  const compareSliderRef = useRef<HTMLImgComparisonSliderElement | null>(null)

  const { isCompact, isTiny, isLandscape } = useDesignLayout()
  const { isPortraitViewport } = useSessionLayout()
  const previewFetchMaxEdgePx = isCompact ? 480 : 720

  /**
   * The booth's locally-tracked strip URL is often a `blob:` URL (S3 upload runs in the
   * background with a 2s race-to-blob fallback so navigation isn't blocked). The server-side
   * `/api/ai/post-session` now resolves the strip from the gallery by `sessionId`, so we pass
   * an HTTPS source URL only when we actually have one — otherwise the server falls back to
   * the gallery's stored strip. This fixes the "strip already exists but AI fails" path.
   */
  const stripSourceUrl = useMemo(() => {
    const items = gallery.capturedItems || []
    const stripItem = items.find((item) => item.id?.includes('dreambooth-strip'))
    const u = stripItem?.stripUrl || gallery.stripUrl || ''
    return u.startsWith('https') ? u : ''
  }, [gallery.capturedItems, gallery.stripUrl])

  /** True when we have evidence the strip was at least produced locally (HTTPS or blob). */
  const hasLocalStripEvidence = useMemo(() => {
    const items = gallery.capturedItems || []
    const stripItem = items.find((item) => item.id?.includes('dreambooth-strip'))
    return Boolean(stripItem?.stripUrl || gallery.stripUrl)
  }, [gallery.capturedItems, gallery.stripUrl])

  const rawItems = useMemo(
    () => collectRawPhotoItems(gallery.capturedItems || []),
    [gallery.capturedItems]
  )

  /** RAW AI is one-shot per session: once any session photo has an AI output, lock further RAW runs. */
  const rawAiUsed = useMemo(
    () => rawItems.some((i) => Boolean(i.aiProcessedPhotoUrl)),
    [rawItems]
  )

  const activeItem = useMemo(() => {
    if (rawItems.length === 0) return undefined
    if (activePhotoId != null) {
      const found = rawItems.find((i) => i.id === activePhotoId)
      if (found) return found
    }
    return rawItems[0]
  }, [activePhotoId, rawItems])

  useEffect(() => {
    if (rawItems.length === 0) {
      setActivePhotoId(null)
      return
    }
    setActivePhotoId((prev) => {
      if (prev != null && rawItems.some((i) => i.id === prev)) return prev
      return rawItems[0]!.id
    })
  }, [rawItems])

  useEffect(() => {
    let cancelled = false
    void (async (): Promise<void> => {
      const next: Record<string, string> = {}
      await Promise.all(
        rawItems.map(async (item) => {
          const src = displaySourceUrl(item.photoUrl)
          if (!src) return
          if (src.startsWith('blob:') || src.startsWith('data:')) {
            next[item.id] = item.photoUrl.startsWith('mirror:') ? src : item.photoUrl
            return
          }
          try {
            next[item.id] = await getCachedImageUrl(src)
          } catch {
            next[item.id] = src
          }
        })
      )
      if (!cancelled) setFilmstripSrcById(next)
    })()
    return (): void => {
      cancelled = true
    }
  }, [rawItems])

  useEffect(() => {
    if (!projectId) return
    if (postAi?.enabled !== true) {
      navigate(`/${projectId}/result`, { replace: true })
    }
  }, [postAi?.enabled, projectId, navigate])

  useEffect(() => {
    if (!projectId || postAi?.enabled !== true) return

    const fromStoreBefore = useConfigStore.getState().config?.pages?.result?.settings?.postSessionAi
      ?.previewBeforeUrl
    const fromStoreAfter = useConfigStore.getState().config?.pages?.result?.settings?.postSessionAi
      ?.previewAfterUrl
    setPreviewBeforeUrl(fromStoreBefore)
    setPreviewAfterUrl(fromStoreAfter)

    let cancelled = false
    setPreviewStatus('loading')

    void (async (): Promise<void> => {
      try {
        const res = await fetch(
          `${process.env.VITE_API_URL}/api/projects?id=${encodeURIComponent(resolvedProjectId ?? '')}`,
          { credentials: 'include' }
        )
        if (!res.ok) {
          if (!cancelled) {
            setPreviewStatus('error')
          }
          return
        }
        const data = (await res.json()) as {
          pages?: {
            result?: {
              settings?: {
                postSessionAi?: { previewBeforeUrl?: string; previewAfterUrl?: string }
              }
            }
          }
        }
        const cfgPostAi = resolvePostSessionAiBooth(data.pages?.result?.settings?.postSessionAi)
        if (!cancelled) {
          setPreviewBeforeUrl(cfgPostAi?.previewBeforeUrl ?? fromStoreBefore)
          setPreviewAfterUrl(cfgPostAi?.previewAfterUrl ?? fromStoreAfter)
          setPreviewStatus('done')
        }
      } catch {
        if (!cancelled) {
          setPreviewStatus('error')
        }
      }
    })()

    return (): void => {
      cancelled = true
    }
  }, [projectId, postAi?.enabled, t])

  const previewHasBoth =
    typeof previewBeforeUrl === 'string' &&
    previewBeforeUrl.length > 0 &&
    typeof previewAfterUrl === 'string' &&
    previewAfterUrl.length > 0

  /**
   * The strip CapturedItem (when present). Used to drive the strip-target session pair
   * in `comparePair` after a successful AI run — its `originalStripUrl` is the pre-AI
   * snapshot and its `stripUrl` is the AI output.
   */
  const stripItem = useMemo(
    () => (gallery.capturedItems || []).find((i) => i.id?.includes('dreambooth-strip')),
    [gallery.capturedItems]
  )

  /**
   * Session photo compare when an AI output exists for the active target; else the
   * dashboard sample pair. Two session-mode shapes are supported so the slider shows
   * the real session result regardless of which target the user picked:
   *
   *   - Raw target: `activeItem.originalPhotoUrl` (or `photoUrl` fallback) vs
   *     `activeItem.aiProcessedPhotoUrl`.
   *   - Strip target: `stripItem.originalStripUrl` vs `stripItem.stripUrl` (the latter
   *     is overwritten with the AI output on success, while the former preserves the
   *     pre-AI strip URL we stamped in the response handler above).
   */
  const comparePair = useMemo((): {
    mode: 'session' | 'dashboard'
    before: string
    after: string
  } | null => {
    if (activeItem?.aiProcessedPhotoUrl) {
      const beforeRaw = activeItem.originalPhotoUrl || activeItem.photoUrl
      const afterRaw = activeItem.aiProcessedPhotoUrl
      const before = typeof beforeRaw === 'string' ? displaySourceUrl(beforeRaw) : ''
      const after = typeof afterRaw === 'string' ? displaySourceUrl(afterRaw) : ''
      if (isEligibleRawPhotoUrl(before) && isEligibleRawPhotoUrl(after)) {
        return { mode: 'session', before, after }
      }
    }
    if (stripItem?.originalStripUrl && stripItem.stripUrl) {
      const before = displaySourceUrl(stripItem.originalStripUrl)
      const after = displaySourceUrl(stripItem.stripUrl)
      if (isEligibleRawPhotoUrl(before) && isEligibleRawPhotoUrl(after) && before !== after) {
        return { mode: 'session', before, after }
      }
    }
    if (previewStatus === 'done' && previewHasBoth) {
      return { mode: 'dashboard', before: previewBeforeUrl, after: previewAfterUrl } as {
        mode: 'dashboard'
        before: string
        after: string
      }
    }
    return null
  }, [
    activeItem,
    stripItem,
    previewBeforeUrl,
    previewAfterUrl,
    previewHasBoth,
    previewStatus,
  ])

  useEffect(() => {
    if (!comparePair) {
      if (previewSliderBlobsRef.current) {
        URL.revokeObjectURL(previewSliderBlobsRef.current.before)
        URL.revokeObjectURL(previewSliderBlobsRef.current.after)
        previewSliderBlobsRef.current = null
      }
      setPreviewSliderBlobs(null)
      setPreviewSliderBlobError(false)
      return
    }

    let cancelled = false
    setPreviewSliderBlobError(false)
    setPreviewSliderBlobs(null)

    const { before, after } = comparePair

    void (async (): Promise<void> => {
      /**
       * `fetchRemoteImageAsBlobUrl` already tries a CORS fetch then the Electron main-process
       * bridge. A leftover failure here is almost always a transient network blip — so retry a
       * couple of times with a short backoff before surfacing any message. Previously a single
       * miss stuck the (CORS-jargon) error on screen until `comparePair` changed.
       */
      const MAX_ATTEMPTS = 3
      let beforeBlob: string | undefined
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          // fetch() supports http(s), blob:, data:, and same-origin paths; returns an owned blob: URL
          beforeBlob = await fetchRemoteImageAsBlobUrl(before, {
            maxEdgePx: previewFetchMaxEdgePx,
          })
          if (cancelled) {
            URL.revokeObjectURL(beforeBlob)
            return
          }
          const afterBlob = await fetchRemoteImageAsBlobUrl(after, {
            maxEdgePx: previewFetchMaxEdgePx,
          })
          if (cancelled) {
            URL.revokeObjectURL(beforeBlob)
            URL.revokeObjectURL(afterBlob)
            return
          }
          if (previewSliderBlobsRef.current) {
            URL.revokeObjectURL(previewSliderBlobsRef.current.before)
            URL.revokeObjectURL(previewSliderBlobsRef.current.after)
          }
          previewSliderBlobsRef.current = { before: beforeBlob, after: afterBlob }
          setPreviewSliderBlobs({ before: beforeBlob, after: afterBlob })
          return
        } catch (err) {
          if (beforeBlob) {
            URL.revokeObjectURL(beforeBlob)
            beforeBlob = undefined
          }
          if (cancelled) return
          if (attempt < MAX_ATTEMPTS) {
            // Keep the loading state — no scary message yet — and back off before retrying.
            await new Promise((r) => setTimeout(r, 500 * attempt))
            if (cancelled) return
            continue
          }
          // Genuine final failure. Keep CORS/host detail in the console only.
          console.warn('[PostSessionAI] preview compare images failed to load', err)
          setPreviewSliderBlobError(true)
        }
      }
    })()

    return (): void => {
      cancelled = true
      if (previewSliderBlobsRef.current) {
        URL.revokeObjectURL(previewSliderBlobsRef.current.before)
        URL.revokeObjectURL(previewSliderBlobsRef.current.after)
        previewSliderBlobsRef.current = null
      }
      setPreviewSliderBlobs(null)
    }
  }, [comparePair, previewFetchMaxEdgePx])

  useEffect(() => {
    setCompareDividerPct(50)
  }, [previewSliderBlobs?.before, previewSliderBlobs?.after])

  useEffect(() => {
    const el = compareSliderRef.current
    if (!el || !previewSliderBlobs || previewSliderBlobError) return
    const sync = (): void => {
      const v = el.value
      if (typeof v === 'number' && Number.isFinite(v)) {
        setCompareDividerPct(Math.max(0, Math.min(100, v)))
      }
    }
    el.addEventListener('slide', sync)
    queueMicrotask(sync)
    return (): void => {
      el.removeEventListener('slide', sync)
    }
  }, [previewSliderBlobs?.before, previewSliderBlobs?.after, previewSliderBlobError])

  // Strip and raw are exclusive — the dashboard surfaces them as a single-choice target and the
  // generated AI outputs are different artifacts that don't share a preview slot. The legacy
  // "both" option no longer exists; any older project doc with `target === "both"` is coerced to
  // "strip" by the dashboard sanitizer before reaching the booth.
  const applyStrip = postAi?.enabled === true && postAi.target === 'strip'
  const applyRaw = postAi?.enabled === true && postAi.target === 'raw'

  /**
   * Enter the review stage once a result exists: the active raw photo has its AI output (raw
   * target), or the strip generation has succeeded (strip target — the active raw item never
   * gets `aiProcessedPhotoUrl`, so we rely on `stripApplied`).
   */
  const showContinue = Boolean(activeItem?.aiProcessedPhotoUrl) || stripApplied

  /** RAW one-shot used up on a different photo than the one currently selected — block Apply. */
  const rawLocked = applyRaw && rawAiUsed && !activeItem?.aiProcessedPhotoUrl

  const goResult = (): void => {
    if (projectId) navigate(`/${projectId}/result`)
  }

  const onSkip = (): void => {
    logActivity('Post-session AI skipped')
    goResult()
  }

  /**
   * Customer's pre-AI photo, shown as the "before" on the select stage (mystery card) and in the
   * payment modal. Raw target: the active item's original (or current) photo. Strip target: the
   * current strip URL (pre-AI). May be a `blob:`/`https:` URL — both render in an <img>.
   */
  const selectedBeforeUrl = useMemo<string | undefined>(() => {
    if (applyRaw) {
      const u = activeItem?.originalPhotoUrl || activeItem?.photoUrl
      return typeof u === 'string' && u ? displaySourceUrl(u) : undefined
    }
    const s = stripItem?.stripUrl || gallery.stripUrl
    return typeof s === 'string' && s ? displaySourceUrl(s) : undefined
  }, [applyRaw, activeItem, stripItem, gallery.stripUrl])

  /**
   * Measured aspect of the strip so the compare frame can match it exactly. With a fixed-shape
   * frame, object-contain letterboxes the tall strip and the (transparent) bars of the BEFORE
   * slot reveal the AFTER image behind it in the img-comparison-slider. Sizing the frame to the
   * real strip aspect removes the bars entirely (contain fills it), so there's no bleed.
   */
  const [stripAspect, setStripAspect] = useState<number | null>(null)
  useEffect(() => {
    if (!applyStrip || !selectedBeforeUrl) {
      setStripAspect(null)
      return
    }
    let cancelled = false
    const img = new Image()
    img.onload = (): void => {
      if (!cancelled && img.naturalWidth > 0 && img.naturalHeight > 0) {
        setStripAspect(img.naturalWidth / img.naturalHeight)
      }
    }
    img.src = selectedBeforeUrl
    return (): void => {
      cancelled = true
    }
  }, [applyStrip, selectedBeforeUrl])

  /**
   * Review-stage "Discard": the customer didn't like the (one-shot) AI result. Revert to the
   * original photo/strip and continue. The single generation is already consumed — Discard does
   * NOT regenerate and (when paid) does not refund; it simply keeps the original.
   */
  const onDiscard = (): void => {
    const prev = useSessionStateStore.getState()
    const g = prev.gallery
    let items = [...(g.capturedItems || [])]
    let nextStripUrl = g.stripUrl

    if (activeItem) {
      items = items.map((it) =>
        it.id === activeItem.id ? { ...it, aiProcessedPhotoUrl: undefined } : it
      )
    }
    const si = items.findIndex((i) => i.id?.includes('dreambooth-strip'))
    if (si >= 0 && items[si].originalStripUrl) {
      nextStripUrl = items[si].originalStripUrl
      items[si] = { ...items[si], stripUrl: items[si].originalStripUrl, originalStripUrl: undefined }
    }

    setSessionState({ gallery: { ...g, stripUrl: nextStripUrl, capturedItems: items } })
    setStripApplied(false)
    logActivity('Post-session AI result discarded')
    if (sessionId) void uploadSessionState(sessionId).catch(() => {})
    goResult()
  }

  /**
   * Dashboard-configurable auto-advance, now shown as a VISIBLE countdown on the intro + select
   * stages (mirrors the payment modal's timer). Disabled when the timeout is 0/undefined; PAUSED
   * (frozen, hidden) while a generation is in flight, once an AI result exists (review stage), or
   * while the payment modal is open. On reaching 0 we skip the AI step and continue.
   */
  const aiTimeoutSec = globalConfig?.pages?.result?.settings?.postSessionAiTimeout ?? 0
  const [remainingTime, setRemainingTime] = useState(aiTimeoutSec)
  // Re-seed if config loads/changes after mount.
  useEffect(() => {
    setRemainingTime(aiTimeoutSec)
  }, [aiTimeoutSec])

  /**
   * Optionally print the AI-enhanced STRIP as an extra print. The original strip is already
   * printed earlier (Select/Edit), so this only fires when the project opts in via
   * `postSessionAiPrintResult`. Strip target only — raw AI never prints. Best-effort and
   * non-blocking: a print failure must not break the AI flow.
   */
  const maybePrintAiStrip = async (aiStripUrl: string): Promise<void> => {
    if (aiStripPrintedRef.current) return
    const printResultEnabled =
      globalConfig?.pages?.result?.settings?.postSessionAiPrintResult === true
    if (!printResultEnabled || postAi?.target !== 'strip') return

    const printState = usePrintStore.getState()
    const copies = userPreferences?.copies ?? 0
    if (!printState.isPrintingEnabled || userPreferences?.usePrint === false || copies <= 0) return

    aiStripPrintedRef.current = true
    try {
      const blob = await (await fetch(aiStripUrl)).blob()
      const { printerName, settings } = printState.getPrinterForFrame(
        userPreferences?.frameId || ''
      )
      await initiatePrint(blob, printerName, copies, settings, isOnline, `ai-${sessionId}`)
      logActivity('Post-session AI strip printed', { printerName, copies })
    } catch (err) {
      aiStripPrintedRef.current = false // allow a retry on the next apply/continue
      logActivity('Post-session AI strip print failed', {
        error: err instanceof Error ? err.message : String(err),
      })
    }
  }

  const onApply = async (): Promise<void> => {
    if (!projectId || !sessionId || !postAi?.enabled) return

    // AI generation is online-only — fail fast with a clear hint instead of a doomed request.
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setStatusHint(null)
      setError(t('result.postSessionAi.offline'))
      return
    }

    /**
     * Strip only re-runs while it hasn't been generated yet — repeated Apply taps (e.g. user
     * iterating across multiple raw photos) shouldn't re-bill the strip generation.
     */
    const wantStripThisCall = applyStrip && !stripApplied

    /**
     * Only block when there's no local evidence of a strip at all. When we *have* a local
     * strip (HTTPS or `blob:`), let the request through — the server resolves the HTTPS URL
     * from the gallery DB by `sessionId` (see `/api/ai/post-session` resolveStripUrlForSession),
     * and returns a retryable 409 ("wait for the upload to finish") if it isn't there yet.
     * The booth's `stripUrl` is almost always a `blob:` (Select.tsx races S3 upload against a
     * 2s timeout and keeps the local blob), so refusing on `!stripSourceUrl` here would block
     * every successful gallery-resolved strip case.
     */
    if (wantStripThisCall && !hasLocalStripEvidence) {
      setError(t('result.postSessionAi.missingStrip'))
      return
    }
    if (applyRaw && rawItems.length === 0) {
      setError(t('result.postSessionAi.missingRaw'))
      return
    }
    // RAW is one-shot: if a session photo already has an AI output and the active one doesn't,
    // block re-running (the user already spent their single "special photo" enhancement).
    if (applyRaw && rawAiUsed && !activeItem?.aiProcessedPhotoUrl) {
      setError(t('result.postSessionAi.rawOnce'))
      return
    }

    /**
     * Apply is per-active-photo: scope raw generation to whichever thumbnail the user has
     * selected in the filmstrip. We send the active item's stable `captureIndex` (assigned at
     * upload time to match the `${index}` in `dreambooth-${sessionId}-photo-${index}.jpg`),
     * which is exactly what the server parses out of each uploaded photo's filename. We fall
     * back to the renderer's `rawItems` position only for legacy sessions captured before
     * `captureIndex` was tracked — but `rawItems.findIndex` is unreliable when the gallery has
     * been re-ordered or extended (strip/gif/mp4/framed-video items) between upload time and
     * now, so always prefer the stored `captureIndex` when present.
     */
    const activeCaptureIndex =
      applyRaw && activeItem
        ? typeof activeItem.captureIndex === 'number'
          ? activeItem.captureIndex
          : rawItems.findIndex((i) => i.id === activeItem.id)
        : -1
    if (applyRaw && activeCaptureIndex < 0) {
      setError(t('result.postSessionAi.missingRaw'))
      return
    }
    // Remember the exact item we are AI-processing so we can attach the result directly when
    // the response arrives, regardless of any intervening gallery mutations. The server only
    // returns the capture index, so without this we'd be relying purely on index math.
    const activeTargetId = applyRaw && activeItem ? activeItem.id : null

    if (!wantStripThisCall && !applyRaw) {
      // Strip-only: strip already generated — Continue navigates to result.
      if (applyStrip && stripApplied) {
        goResult()
        return
      }
      setError(t('result.postSessionAi.errorGeneric'))
      return
    }

    setError(null)
    setBusy(true)
    const generationRequestId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`

    /**
     * Server resolves raw photos from the gallery DB by `sessionId`; the booth never sends
     * `rawSourceUrls` because it only has local blob/data URLs. The server returns each AI
     * output paired with the originating capture index so we can attach it to the right
     * `capturedItems[i]` regardless of upload completion order.
     */
    const body: Record<string, unknown> = {
      projectId: resolvedProjectId,
      sessionId,
      generationRequestId,
      applyStrip: wantStripThisCall,
      applyRaw,
      tier: configuredTier,
    }
    // Only attach `stripSourceUrl` when it's an HTTPS URL. When omitted, the server falls back
    // to gallery-DB resolution by `sessionId` (modern path). The Zod schema rejects empty strings
    // because it validates `.url()`, so we must not set the key when `stripSourceUrl` is `''`.
    if (wantStripThisCall && stripSourceUrl) body.stripSourceUrl = stripSourceUrl
    if (applyRaw && activeCaptureIndex >= 0) {
      body.rawCaptureIndices = [activeCaptureIndex]
    }

    /**
     * Per-ATTEMPT abort. One controller shared across the retry loop meant the 409 retries
     * (plus their delays) ate the same budget as the generation itself, so a request that
     * retried twice had barely any time left to actually generate — and an abort mid-flight
     * leaves the server generating (and charging) an image the booth then discards.
     */
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null
    const clearAttemptTimeout = (): void => {
      if (timeoutHandle) clearTimeout(timeoutHandle)
      timeoutHandle = null
    }

    type PostSessionAiResponse = {
      error?: string
      code?: string
      stripOutputUrls?: string[]
      // Canonical HTTPS URL of the strip the server used as the AI input. Stored as
      // `originalStripUrl` on the strip CapturedItem so the compare slider can show a real
      // session "before vs after" pair for strip-target generation.
      stripSourceUrl?: string | null
      rawOutputUrls?: string[]
      rawOutputs?: Array<{ captureIndex: number; url: string }>
    }

    try {
      let res!: Response
      let data: PostSessionAiResponse = {}
      // A blob-only strip can momentarily 409 ("strip still uploading") while its S3 upload
      // finishes server-side; retry a couple of times (showing a hint) before erroring.
      for (let attempt = 0; ; attempt++) {
        const controller =
          typeof AbortController !== 'undefined' ? new AbortController() : null
        timeoutHandle = controller
          ? setTimeout(() => controller.abort(), AI_GENERATION_TIMEOUT_MS)
          : null
        try {
          res = await fetch(`${process.env.VITE_API_URL}/api/ai/post-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(body),
            signal: controller?.signal,
          })
          data = (await res.json().catch(() => ({}))) as PostSessionAiResponse
        } finally {
          clearAttemptTimeout()
        }
        /**
         * Only the "still uploading" 409s are worth retrying. `raw_already_used` is a
         * TERMINAL 409 (the session already spent its one-shot RAW effect) — retrying it
         * just burned the guest's time on two guaranteed-identical rejections.
         */
        const isRetryable409 = res.status === 409 && data.code !== 'raw_already_used'
        if (isRetryable409 && attempt < STRIP_UPLOAD_RETRY_MAX) {
          setStatusHint(t('result.postSessionAi.stripUploading'))
          await new Promise((r) => setTimeout(r, STRIP_UPLOAD_RETRY_DELAY_MS))
          continue
        }
        break
      }
      setStatusHint(null)

      if (res.status === 403) {
        setError(t('result.postSessionAi.insufficientCredits'))
        setBusy(false)
        return
      }
      if (data.code === 'raw_already_used') {
        // Server enforces the one-shot RAW rule too; show the same friendly hint.
        setError(t('result.postSessionAi.rawOnce'))
        setBusy(false)
        return
      }
      if (!res.ok) {
        setError(data.error || t('result.postSessionAi.errorGeneric'))
        setBusy(false)
        return
      }

      const stripOut = data.stripOutputUrls?.[0]
      /**
       * Prefer the indexed shape; fall back to positional mapping for older servers. The
       * positional fallback maps `rawOutputUrls[i]` to `rawItems[i]` in capture order — only
       * reliable if all uploads finished and the gallery preserved capture order.
       */
      const rawOutputsIndexed: Array<{ captureIndex: number; url: string }> =
        Array.isArray(data.rawOutputs) && data.rawOutputs.length > 0
          ? data.rawOutputs
          : (data.rawOutputUrls ?? []).map((url, i) => ({ captureIndex: i, url }))

      {
        const prev = useSessionStateStore.getState()
        const g = prev.gallery
        const items = [...(g.capturedItems || [])]
        let nextStripUrl = g.stripUrl

        if (stripOut) {
          nextStripUrl = stripOut
          const si = items.findIndex((i) => i.id?.includes('dreambooth-strip'))
          if (si >= 0) {
            /**
             * Preserve the pre-AI strip URL the first time we overwrite `stripUrl` with
             * an AI output, so the compare slider can render the actual session strip
             * before/after pair (mirroring `originalPhotoUrl` for raw items). Prefer the
             * canonical HTTPS URL the server confirms it used; fall back to the booth's
             * existing local URL (often `blob:` — still renderable in the slider) so the
             * slider keeps working even on older builds where `stripSourceUrl` isn't sent.
             */
            const priorStripUrl =
              items[si].originalStripUrl ??
              (typeof data.stripSourceUrl === 'string' && data.stripSourceUrl
                ? data.stripSourceUrl
                : items[si].stripUrl ?? g.stripUrl ?? undefined)
            items[si] = {
              ...items[si],
              stripUrl: stripOut,
              originalStripUrl: priorStripUrl,
            }
          }
        }

        /**
         * Map each AI output URL back to the originating gallery item. The server returns the
         * filename-derived `captureIndex`; we resolve it against the live `items` array in this
         * priority order:
         *   1. The id we captured before the request (`activeTargetId`) — bulletproof for the
         *      booth's single-photo Apply flow because the user can only generate one at a time.
         *   2. The item whose persisted `captureIndex` field equals the response's index — works
         *      for any future multi-photo flow and for legacy items that already finished
         *      uploading (since upload now stamps `captureIndex` on each item).
         *   3. `rawItems[captureIndex]` as a last resort for very old sessions captured before
         *      `captureIndex` was tracked — same behavior as before this fix.
         */
        const attachedItemIds = new Set<string>()
        if (rawOutputsIndexed.length > 0) {
          for (const { captureIndex, url } of rawOutputsIndexed) {
            let ix = -1
            if (activeTargetId && !attachedItemIds.has(activeTargetId)) {
              ix = items.findIndex((it) => it.id === activeTargetId)
            }
            if (ix < 0) {
              ix = items.findIndex(
                (it) => typeof it.captureIndex === 'number' && it.captureIndex === captureIndex
              )
            }
            if (ix < 0 && rawItems.length > 0) {
              const fallbackTarget = rawItems[captureIndex]
              if (fallbackTarget) {
                ix = items.findIndex((it) => it.id === fallbackTarget.id)
              }
            }
            if (ix >= 0) {
              items[ix] = { ...items[ix], aiProcessedPhotoUrl: url }
              attachedItemIds.add(items[ix].id)
            }
          }
        }

        setSessionState({
          gallery: {
            ...g,
            stripUrl: nextStripUrl,
            capturedItems: items,
          },
        })
      }

      if (stripOut) {
        setStripApplied(true)
        // Optional extra print of the AI strip (project opt-in). Non-blocking.
        void maybePrintAiStrip(stripOut)
      }

      logActivity('Post-session AI applied', {
        applyStrip: wantStripThisCall,
        applyRaw,
        strip: Boolean(stripOut),
        rawCount: rawOutputsIndexed.length,
        activeCaptureIndex: activeCaptureIndex >= 0 ? activeCaptureIndex : undefined,
        tier: configuredTier,
      })

      void uploadSessionState(sessionId).catch(() => {})

      // Stay on page so the preview slider can swap to the AI before/after for the active
      // photo (or the strip indicator becomes "ready"). User taps Continue to proceed.
    } catch (err) {
      setStatusHint(null)
      // Our own timeout aborts the request — tell the user it's slow rather than "failed".
      if (err instanceof DOMException && err.name === 'AbortError') {
        setError(t('result.postSessionAi.timeout'))
      } else {
        setError(t('result.postSessionAi.errorGeneric'))
      }
    } finally {
      clearAttemptTimeout()
      setBusy(false)
    }
  }

  /**
   * Optional paid AI effect (real money). When the dashboard enables `postSessionAiPaymentEnabled`
   * and has packages, tapping Apply opens the payment modal first; generation only runs after the
   * customer pays (gateway/voucher) or an operator bypasses with PIN. Payment is once per session.
   */
  const aiPaymentEnabled =
    globalConfig?.pages?.result?.settings?.postSessionAiPaymentEnabled === true
  const aiPackages = useMemo(
    () => globalConfig?.pages?.result?.settings?.postSessionAiPackages || [],
    [globalConfig?.pages?.result?.settings?.postSessionAiPackages]
  )
  const aiAlreadyPaid =
    transaction?.aiEffectPaymentStatus === 'settlement' ||
    transaction?.aiEffectPaymentStatus === 'bypass'
  const [aiPaidLocal, setAiPaidLocal] = useState(false)
  const [showAiPayment, setShowAiPayment] = useState(false)
  const aiPaid = aiPaidLocal || aiAlreadyPaid
  // Only charge the guest when their cheapest active package actually exceeds the AI credit
  // cost; otherwise the effect is free (owner credits) and we skip the payment prompt. Mirrors
  // the server in /api/ai/post-session. Non-IDR packages keep guest-pays (no credit→IDR rate).
  const aiGuestPays = useMemo(() => {
    // Digital mode has no checkout/payment — the AI effect is always free to
    // the consumer (runs on the owner's credits). Never prompt for payment.
    if (isDigitalMode()) return false
    if (!aiPaymentEnabled) return false
    const active = aiPackages.filter(
      (p) => p?.isActive !== false && typeof p.price === 'number'
    )
    if (active.length === 0) return false
    const idr = active.filter((p) => !p.currency || String(p.currency).toUpperCase() === 'IDR')
    if (idr.length < active.length) return true
    const cheapest = Math.min(...idr.map((p) => p.price as number))
    const creditCostIdr = (AI_TIER_CREDITS[configuredTier] ?? AI_TIER_CREDITS.fast) * AI_CREDIT_PRICE_IDR
    return cheapest > creditCostIdr
  }, [aiPaymentEnabled, aiPackages, configuredTier])
  const aiPaymentRequired = aiGuestPays && !aiPaid

  /**
   * Idle countdown control. Runs on the intro, select AND review stages; frozen only while
   * generating (`busy`) or while the payment modal is open. Reaching 0 continues to the result —
   * on the review stage that keeps the AI result (the same as tapping Continue), since the output
   * is already applied to the session.
   */
  const countdownPaused = busy || showAiPayment
  const showCountdown = aiTimeoutSec > 0 && !countdownPaused
  // Restart the timer when entering the review stage so the guest gets a fresh window to decide.
  useEffect(() => {
    if (showContinue && !busy) setRemainingTime(aiTimeoutSec)
  }, [showContinue, busy, aiTimeoutSec])
  useEffect(() => {
    if (aiTimeoutSec <= 0 || countdownPaused || !projectId) return
    const handle = setInterval(() => {
      setRemainingTime((prev) => (prev <= 1 ? 0 : prev - 1))
    }, 1000)
    return (): void => clearInterval(handle)
  }, [aiTimeoutSec, countdownPaused, projectId])
  useEffect(() => {
    if (aiTimeoutSec <= 0 || countdownPaused || !projectId) return
    if (remainingTime <= 0) {
      logActivity('Post-session AI timed out')
      navigate(`/${projectId}/result`)
    }
  }, [remainingTime, aiTimeoutSec, countdownPaused, projectId, navigate, logActivity])

  const requestApply = (): void => {
    if (aiPaymentRequired) {
      setShowAiPayment(true)
      return
    }
    void onApply()
  }

  const handleAiPaid = (info: AiEffectPaidInfo): void => {
    setShowAiPayment(false)
    setAiPaidLocal(true)
    setSessionState({
      transaction: {
        aiEffectPaymentStatus: info.via === 'bypass' ? 'bypass' : 'settlement',
        aiEffectAmount: info.amount,
        aiEffectPackageLabel: info.label,
      },
    })
    logActivity('AI effect purchased', { amount: info.amount, label: info.label, via: info.via })
    if (sessionId) void uploadSessionState(sessionId).catch(() => {})
    void onApply()
  }

  const configLoading = previewStatus === 'loading' || previewStatus === 'idle'

  /**
   * Images for the compare slider. Prefer the downscaled, same-origin blob URLs when the blob
   * conversion succeeded (cheaper for the slider to composite), but fall back to the *direct*
   * before/after URLs whenever blobs aren't available. This is the fix for "could not load
   * preview on AI effects": when the booth runs in a plain browser (e.g. app.dreamboothstudio.com)
   * there is no Electron main-process bridge, and `cdn.dreambooth.app` doesn't return CORS headers,
   * so `fetch()->blob` throws. But plain <img> rendering is NOT subject to CORS, so the direct URLs
   * always display. The blob path is now an optimization, never a hard requirement.
   */
  const compareImages = useMemo(
    () =>
      previewSliderBlobs ??
      (comparePair ? { before: comparePair.before, after: comparePair.after } : null),
    [previewSliderBlobs, comparePair]
  )
  const showCompareChrome = Boolean(compareImages)
  // Config is still being fetched and we have nothing to show yet.
  const showConfigLoading = Boolean(!comparePair && configLoading)

  // Measure the real BEFORE image aspect so the compare box fits ANY photo shape
  // (frame slot, raw individual capture, or a marketing preview) with no crop. The
  // AFTER shares this aspect since the server crops it to the input. Falls back to
  // framePhotoRatio / 3:4 until decoded. Uses `decode()`-free onload so a broken URL
  // just leaves the fallback in place.
  const compareBeforeSrc = compareImages?.before
  useEffect(() => {
    setRawImgRatio(null)
    if (applyStrip || !compareBeforeSrc) return
    let cancelled = false
    const img = new Image()
    img.onload = (): void => {
      if (!cancelled && img.naturalWidth > 0 && img.naturalHeight > 0) {
        setRawImgRatio(img.naturalWidth / img.naturalHeight)
      }
    }
    img.src = compareBeforeSrc
    return (): void => {
      cancelled = true
    }
  }, [compareBeforeSrc, applyStrip])

  const applyAlert = useMemo(() => (error ? { text: error } : null), [error])

  if (!projectId || postAi?.enabled !== true) {
    return null
  }

  /** High slider `value` = more before (first slot) visible — hide the After chip; inverse for Before. */
  const showCompareBeforeChip = compareDividerPct >= 50
  const showCompareAfterChip = compareDividerPct <= 50

  /**
   * Portrait: glass preview above grid, stacked full-width. Applies to ANY portrait
   * canvas, not just compact phones — at a spacious booth (1080×1920) the two-column
   * split left the photo grid clustered small in the top-left with most of the screen
   * empty (result deep-dive). Stacking fills the tall canvas; landscape is unchanged.
   */
  const stackPreviewFirst = isPortraitViewport

  const previewColumnClass = applyStrip
    ? // Strip target has no photo grid — a COMPACT modal that hugs its content and
      // centers on the page (m-auto in the flex-col parent), instead of a full-height
      // column that spread the title/preview/buttons apart with dead bands between
      // them (owner review). Mirrors the consent modal.
      // Booth monitors get a WIDER card so the preview fills the tall screen;
      // mobile stays modest (parent p-4 gives edge padding); desktop a compact card.
      `m-auto w-full min-w-0 ${
        isTiny ? 'max-w-[94vw]' : isLandscape ? 'max-w-[min(94vw,560px)]' : 'max-w-[min(92vw,720px)]'
      }`
    : stackPreviewFirst
      ? // The column MUST stay bounded and shrinkable. It previously carried
        // `shrink-0 sm:max-h-none`, which removed the only height cap on every viewport
        // ≥640px — i.e. every real booth — while forbidding the column from shrinking. The
        // panel then grew to its content height (a 780px preview box + title + buttons), the
        // Apply/Skip row at its bottom was pushed past the fold, and the parent has no
        // `overflow-y`, so there was nothing to scroll: "Apply AI nggak kelihatan kalau
        // fullscreen, nggak bisa di-scroll". The `sm:` cap is generous (a portrait booth
        // still gets a tall preview) but finite, so the buttons are always reachable.
        'w-full min-h-[220px] max-h-[min(55dvh,480px)] sm:max-h-[62dvh]'
      : `w-full min-w-0 flex-1 ${isPortraitViewport ? 'sm:max-w-[58%]' : 'sm:max-w-[40%]'}`

  const gridColumnClass = stackPreviewFirst
    ? 'w-full min-h-0 flex-1'
    : 'w-full min-h-0 flex-1 sm:max-w-[65%]'

  const glassPadding = stackPreviewFirst ? 'p-2' : 'p-4 sm:p-8'

  /**
   * Per-tier sizing for the AI-effect stages (intro / processing / review and the select panel),
   * mirroring the consent + reprint modals. Buttons are big on a monitor but not palm-sized, and
   * stay HORIZONTAL on phones instead of stacking. Tiers: tiny = small phone, compact = phone /
   * portrait monitor, spacious = large monitor.
   */
  // Small screens (phone / small kiosk) go big + fill; large screens (laptop / monitor) stay a
  // modest, centered card — so button + text + preview sizes are capped, not scaled up.
  const fillMode = isTiny || isCompact
  const aiBtnSize = isTiny
    ? '!px-4 !py-2 !text-sm !leading-snug'
    : isCompact
      ? '!px-5 !py-2.5 !text-base !leading-snug'
      : '!px-6 !py-3 !text-base !leading-snug' // large monitors: touch-clickable, not palm-sized
  const aiBtnRow = `flex w-full flex-row flex-wrap items-center justify-center ${
    isTiny ? 'gap-2' : isCompact ? 'gap-2.5' : 'gap-3'
  }`
  // Panel sizing/padding/gap lives in <AiStageShell>; the tokens below style the content each
  // stage passes into the shell — kept MODEST on large screens (a centered card, not blown up).
  const aiHeadingClass = isTiny ? 'text-2xl' : isCompact ? 'text-3xl' : 'text-4xl'
  const aiBodyClass = isTiny ? 'text-sm' : isCompact ? 'text-base' : 'text-lg'

  /**
   * Strip target shows the WHOLE tall strip (object-contain in a tall box), never cropped to 3:4.
   * Raw target keeps the 3:4 portrait crop that suits a single photo.
   *
   * Strip preview height: fill big on small screens; keep MODEST on large so the panel stays a
   * centered card instead of a stretched full-height block.
   */
  // The design tier at a 1080×1920 booth resolves to "spacious" (a large monitor),
  // which the original code treated as a modest desktop card — but a large PORTRAIT
  // monitor is a booth and should be FILLED (owner review). Key off orientation, not
  // just the fill tier: any non-mobile PORTRAIT gets a tall preview; mobile stays
  // modest (room for the parent's p-4 padding); landscape keeps its sizes.
  const stripFrameH = isLandscape
    ? fillMode
      ? 'h-[min(78dvh,560px)]'
      : 'h-[min(64dvh,460px)]'
    : isTiny
      ? 'h-[min(52dvh,440px)]'
      : 'h-[min(66dvh,860px)]'
  /**
   * Raw (single-photo) preview box uses the ACTUAL captured-photo aspect ratio, NOT a
   * hardcoded 3:4. The post-session AI output is center-cropped server-side to match the
   * input photo's aspect exactly (dreambooth `postSessionAiImage.ts`), so BEFORE and
   * AFTER always share ONE ratio — the right thing to display is that ratio itself, with
   * both images object-cover it (no crop, no letterbox). `framePhotoRatio` is the frame's
   * photo-slot ratio (e.g. square 1:1, 3:4, 4:5) picked at capture; default 3:4.
   *
   * The single `min()` caps HEIGHT (booth fill) and WIDTH (`92vw`, so mobile / landscape
   * photos never overflow) while preserving the true ratio. A DEFINITE height is required
   * because the img-comparison-slider sizes its base (`second`) slot to the image — so the
   * box lives on the frame AND on the AFTER <img> (via style) so both share one rectangle.
   */
  const validRatio = (r: number | null | undefined): r is number =>
    typeof r === 'number' && r > 0.2 && r < 5
  // Prefer the MEASURED image aspect (ground truth for the raw/individual-photo effect,
  // which isn't bound to a frame slot); fall back to the frame slot ratio, then 3:4.
  const rawRatio = validRatio(rawImgRatio)
    ? rawImgRatio
    : validRatio(userPreferences.framePhotoRatio)
      ? userPreferences.framePhotoRatio
      : 3 / 4
  const rawHDvh = isLandscape ? '58dvh' : isTiny ? '50dvh' : '66dvh'
  const rawHPx = isLandscape ? '460px' : isTiny ? '430px' : '780px'
  const rawBoxStyle: React.CSSProperties = {
    aspectRatio: String(rawRatio),
    height: `min(${rawHDvh}, ${rawHPx}, calc(92vw / ${rawRatio}))`
  }
  const previewFrameClass = applyStrip
    ? // Once the strip aspect is known, drive width from the fixed height so the frame matches the
      // strip exactly (no letterbox → no slider bleed). Until then, fall back to a capped box.
      `relative mx-auto ${stripFrameH} overflow-hidden rounded-xl bg-black/[0.03] ring-1 ring-black/10 ${
        stripAspect ? 'w-auto max-w-full' : 'w-full max-w-[min(86vw,480px)]'
      }`
    : 'relative mx-auto w-auto overflow-hidden rounded-xl bg-black/[0.03] ring-1 ring-black/10'
  const previewFrameStyle: React.CSSProperties | undefined = applyStrip
    ? stripAspect
      ? { aspectRatio: String(stripAspect) }
      : undefined
    : rawBoxStyle
  const previewImgFit = applyStrip ? 'object-contain' : 'object-cover'
  // The library sizes its base (`second`) slot to the IMAGE, ignoring the slot's own
  // height — so the definite box must live on the AFTER <img> itself (via rawBoxStyle).
  const compareSecondSlotClass = applyStrip ? 'relative block h-full w-full' : 'relative block overflow-hidden'
  const compareAfterImgClass = applyStrip ? 'h-full w-full' : 'w-auto object-cover'

  /**
   * The compare slider (or its loading/empty fallback) for the 3:4 frame. Extracted so both the
   * intro hero and the working preview panel render the exact same thing without duplicating the
   * slider + before/after chip markup.
   */
  const compareFrameInner =
    showCompareChrome && compareImages ? (
      <>
        <ImgComparisonSlider
          ref={compareSliderRef}
          className="relative z-0 h-full w-full max-w-full [--divider-width:4px] [--default-handle-color:rgba(255,255,255,0.95)] [--default-handle-width:36px] [--default-handle-shadow:0_1px_6px_rgba(0,0,0,0.3)]"
          style={{ ['--divider-color' as string]: themePrimary } as React.CSSProperties}
        >
          {/* `.first-overlay-container` from the library already has height: 100%, so the first
              slot wrapper can fill via absolute/inset-0. `.second` only has `position: relative`
              with no height, so the second slot wrapper needs its own dimensions — we match the
              outer 3:4 frame so both images render in identical boxes. */}
          {/* Opaque bg so any residual letterbox gap shows neutral, never the AFTER image behind. */}
          <div slot="first" className="absolute inset-0 block bg-black/[0.03]">
            <LoadableImage
              className={`h-full w-full ${previewImgFit}`}
              src={compareImages.before}
              alt={t('result.postSessionAi.previewBefore')}
            />
          </div>
          <div slot="second" className={compareSecondSlotClass}>
            <LoadableImage
              className={`${compareAfterImgClass} ${previewImgFit}`}
              style={applyStrip ? undefined : rawBoxStyle}
              src={compareImages.after}
              alt={t('result.postSessionAi.previewAfter')}
            />
          </div>
        </ImgComparisonSlider>
        <span
          className={`pointer-events-none absolute left-2 top-2 z-10 rounded bg-black/45 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white antialiased shadow-sm transition-opacity duration-150 ${showCompareBeforeChip ? 'opacity-100' : 'opacity-0'}`}
          aria-hidden={!showCompareBeforeChip}
        >
          {t('result.postSessionAi.previewBefore')}
        </span>
        <span
          className={`pointer-events-none absolute right-2 top-2 z-10 rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide antialiased text-white shadow-sm transition-opacity duration-150 ${showCompareAfterChip ? 'opacity-100' : 'opacity-0'}`}
          style={{ backgroundColor: themePrimary }}
          aria-hidden={!showCompareAfterChip}
        >
          {t('result.postSessionAi.previewAfter')}
        </span>
      </>
    ) : showConfigLoading ? (
      <div className="flex h-full min-h-[120px] items-center justify-center px-2">
        <p className="text-xs text-carbon/50">{t('result.postSessionAi.previewLoading')}</p>
      </div>
    ) : (
      <div className="flex h-full min-h-[120px] items-center justify-center px-4 text-center text-xs text-carbon/50">
        {t('result.postSessionAi.previewEmptyMessage')}
      </div>
    )

  /**
   * Show the intro hero only when there's a real dashboard example to lead with and the guest
   * hasn't acted yet. If the project configured no preview sample (comparePair is null or a
   * session result), fall straight through to the working layout — there's nothing to explain.
   */
  const showIntro =
    !introAcknowledged && !showContinue && comparePair?.mode === 'dashboard'

  const introPanel = (
    <AiStageShell
      header={
        <>
          <span
            className="rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white"
            style={{ background: themePrimary }}
          >
            {t('result.postSessionAi.introBadge')}
          </span>
          <div className="flex flex-col items-center gap-1">
            <h1 className={`text-center font-bold leading-tight text-carbon ${aiHeadingClass}`}>
              {introTitle}
            </h1>
            <p className={`max-w-prose text-center leading-snug text-carbon/60 ${aiBodyClass}`}>
              {introDescription}
            </p>
          </div>
        </>
      }
      preview={
        <div className={previewFrameClass} style={previewFrameStyle}>
          {compareFrameInner}
        </div>
      }
      footer={
        <div className={`${aiBtnRow} pb-[max(0.25rem,env(safe-area-inset-bottom))]`}>
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={onSkip}
            text={t('result.postSessionAi.skip')}
            className={aiBtnSize}
          />
          <Button
            type="button"
            onClick={() => setIntroAcknowledged(true)}
            text={t('result.postSessionAi.introCta')}
            className={aiBtnSize}
          />
        </div>
      }
    />
  )

  const gridSection = (
        <section
          className={`custom-scrollbar flex min-h-0 flex-col overflow-y-auto p-5 ${gridColumnClass}`}
          aria-label={t('result.postSessionAi.filmstripAriaLabel')}
        >
          {rawItems.length === 0 ? (
            <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-black/10 bg-white/70 py-12 text-sm text-carbon/60">
              {t('result.postSessionAi.previewEmptyMessage')}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
              {rawItems.map((item) => {
                const selected = activeItem?.id === item.id
                const hasAi = Boolean(item.aiProcessedPhotoUrl)
                const displayUrl = displaySourceUrl(item.photoUrl)
                const thumbSrc = filmstripSrcById[item.id] ?? displayUrl
                const showThumbSkeleton =
                  filmstripSrcById[item.id] == null &&
                  (displayUrl.startsWith('http://') || displayUrl.startsWith('https://'))

                return (
                  <button
                    key={item.id}
                    type="button"
                    disabled={busy}
                    onClick={() => setActivePhotoId(item.id)}
                    aria-label={hasAi ? t('result.postSessionAi.filmstripThumbWithAi') : t('result.postSessionAi.filmstripThumb')}
                    className={[
                      'group relative overflow-hidden rounded-xl border-2 transition-all duration-150',
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400',
                      selected ? 'shadow-md' : 'border-transparent opacity-70 hover:opacity-90',
                    ].join(' ')}
                    style={selected ? { borderColor: themePrimary } : undefined}
                  >
                    <div className="aspect-[3/4] w-full bg-black/5">
                      {showThumbSkeleton ? (
                        <div className="h-full w-full animate-pulse bg-black/10" />
                      ) : (
                        <img
                          src={thumbSrc}
                          alt=""
                          className="h-full w-full object-cover"
                          draggable={false}
                        />
                      )}
                    </div>

                    {selected ? (
                      <span
                        className="pointer-events-none absolute top-2 left-2 flex h-6 w-6 items-center justify-center rounded-full ring-2 ring-white/80"
                        style={{ background: themePrimary }}
                        aria-hidden
                      >
                        <svg className="h-3.5 w-3.5 text-white" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M2 6l3 3 5-5"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    ) : null}

                    {hasAi ? (
                      <span
                        title={t('result.postSessionAi.aiProcessedTooltip')}
                        className="pointer-events-none absolute top-2 right-2 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white/90"
                        aria-hidden
                      />
                    ) : null}
                  </button>
                )
              })}
            </div>
          )}
        </section>
  )

  /** Select stage right panel: intro title/desc + the customer's photo with a mystery "after". */
  const previewPanel = (
        <div className={`flex min-h-0 min-w-0 flex-col ${previewColumnClass}`}>
          <GlassPanel
            className={`flex min-h-0 w-full flex-col overflow-hidden ${glassPadding} ${
              applyStrip ? 'h-auto max-h-[92dvh]' : 'h-full flex-1'
            }`}
          >
            <div
              className={`flex w-full shrink-0 flex-col items-center ${
                stackPreviewFirst ? 'mt-0 gap-1 px-1' : 'mt-2 gap-3 px-2 sm:mt-6 sm:gap-4'
              }`}
            >
              <h1
                className={`text-center font-bold text-carbon ${stackPreviewFirst ? 'text-lg leading-tight' : 'text-xl sm:text-3xl'}`}
              >
                {introTitle}
              </h1>
              <p
                className={`max-w-full text-center leading-snug text-carbon/60 ${
                  stackPreviewFirst ? 'px-1 text-xs' : 'px-2 text-sm sm:px-4 sm:text-base'
                }`}
              >
                {introDescription}
              </p>
            </div>

            <div
              className={`custom-scrollbar flex min-h-0 flex-col items-center justify-center overflow-y-auto px-1 py-2 ${
                applyStrip ? 'shrink-0' : 'flex-1'
              }`}
            >
              {/* `max-h-full` lets the definite-height preview box scale down inside the now-
                  bounded panel instead of overflowing it. Without it a box taller than its
                  scroll area gets clipped at BOTH ends by the wrapper's `justify-center`,
                  which is unreachable by scrolling. Select stage only — the review stage's
                  compare slider keeps its exact rectangle. */}
              <div className={`${previewFrameClass} max-h-full min-h-0`} style={previewFrameStyle}>
                <MysteryAfter
                  beforeUrl={selectedBeforeUrl}
                  themePrimary={themePrimary}
                  beforeLabel={t('result.postSessionAi.previewBefore')}
                  afterLabel={t('result.postSessionAi.previewAfter')}
                  objectFit={previewImgFit === 'object-contain' ? 'contain' : 'cover'}
                />
              </div>
            </div>

            <div className="mt-3 flex w-full shrink-0 flex-col gap-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-3">
              {applyAlert ? (
                <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-800" role="alert">
                  {applyAlert.text}
                </p>
              ) : null}
              {!applyAlert && (statusHint || !isOnline) ? (
                <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-800" role="status">
                  {statusHint ?? t('result.postSessionAi.offline')}
                </p>
              ) : null}
              {rawLocked && !applyAlert && !statusHint && isOnline ? (
                <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-800" role="status">
                  {t('result.postSessionAi.rawOnce')}
                </p>
              ) : null}
              <div className={aiBtnRow}>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={busy}
                  onClick={onSkip}
                  text={t('result.postSessionAi.skip')}
                  className={aiBtnSize}
                />
                <Button
                  type="button"
                  // Apply needs the network; block when offline or the one-shot RAW is used up.
                  disabled={busy || rawLocked || !isOnline}
                  onClick={requestApply}
                  text={busy ? t('result.postSessionAi.applying') : t('result.postSessionAi.apply')}
                  className={aiBtnSize}
                />
              </div>
            </div>
          </GlassPanel>
        </div>
  )

  /** Processing stage: single centered photo with a scan/shimmer animation while AI runs.
   * The scan + shimmer IS the progress indicator — no spinner. Copy mirrors the intro/select
   * (title + description) for a consistent identity, with a small pulsing status line. */
  const processingPanel = (
    <AiStageShell
      preview={
        <div className={previewFrameClass} style={previewFrameStyle}>
          <LoadableImage
            src={selectedBeforeUrl}
            alt={t('result.postSessionAi.previewBefore')}
            className={`absolute inset-0 h-full w-full ${previewImgFit}`}
          />
          <div className="absolute inset-0 bg-black/10" />
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-1/3 animate-ai-scan"
            style={{ background: `linear-gradient(180deg, transparent, ${themePrimary}66, transparent)` }}
          />
          <div
            className="absolute inset-0 animate-ai-shimmer"
            style={{
              backgroundImage:
                'linear-gradient(110deg, transparent 40%, rgba(255,255,255,0.35) 50%, transparent 60%)',
              backgroundSize: '250% 100%',
            }}
          />
        </div>
      }
      header={
        <div className="flex flex-col items-center gap-1">
          <h1 className={`text-center font-bold leading-tight text-carbon ${aiHeadingClass}`}>
            {introTitle}
          </h1>
          <p className={`max-w-prose text-center leading-snug text-carbon/60 ${aiBodyClass}`}>
            {introDescription}
          </p>
          <p className="mt-1 animate-pulse text-center text-xs font-medium text-carbon/40">
            {t('result.postSessionAi.processing')}
          </p>
        </div>
      }
    />
  )

  /** Review stage: celebration + real before/after slider with Keep / Discard. */
  const reviewPanel = (
    <AiStageShell
      panelClassName="animate-ai-pop"
      overlay={
        // One-shot sparkle burst on reveal
        <div className="pointer-events-none absolute inset-x-0 top-3 z-10 flex justify-center gap-6" aria-hidden>
          <span className="animate-ai-spark text-2xl">✨</span>
          <span className="animate-ai-spark text-3xl" style={{ animationDelay: '0.1s' }}>🎉</span>
          <span className="animate-ai-spark text-2xl" style={{ animationDelay: '0.2s' }}>✨</span>
        </div>
      }
      preview={
        <div className={previewFrameClass} style={previewFrameStyle}>
          {compareFrameInner}
        </div>
      }
      header={
        <>
          <span
            className="rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white"
            style={{ background: themePrimary }}
          >
            {t('result.postSessionAi.introBadge')}
          </span>
          <div className="flex flex-col items-center gap-1">
            <h1 className={`text-center font-bold leading-tight text-carbon ${aiHeadingClass}`}>
              {introTitle}
            </h1>
            <p className={`max-w-prose text-center leading-snug text-carbon/60 ${aiBodyClass}`}>
              {introDescription}
            </p>
          </div>
        </>
      }
      footer={
        <>
          <p className="text-center text-xs text-carbon/45">
            {t('result.postSessionAi.resultMayVary')}
          </p>
          {applyAlert ? (
            <p className="w-full rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-800" role="alert">
              {applyAlert.text}
            </p>
          ) : null}
          <div className={`${aiBtnRow} pb-[max(0.25rem,env(safe-area-inset-bottom))]`}>
            <Button
              type="button"
              variant="secondary"
              onClick={onDiscard}
              text={t('result.postSessionAi.discard')}
              className={aiBtnSize}
            />
            <Button
              type="button"
              onClick={goResult}
              text={t('result.postSessionAi.continue')}
              className={aiBtnSize}
            />
          </div>
        </>
      }
    />
  )

  // Explicit stage machine. Order matters: a running generation always shows processing; a
  // produced result shows review; otherwise lead with the intro example (when configured), then
  // the photo picker.
  const stage: 'intro' | 'select' | 'processing' | 'review' = busy
    ? 'processing'
    : showContinue
      ? 'review'
      : showIntro
        ? 'intro'
        : 'select'

  return (
    <BackgroundContainer className="relative flex h-full min-h-0 flex-col">
      {/* Visible idle countdown — intro, select and review; frozen/hidden while generating.
          Was tiny low-contrast grey text in the far page corner (audit: "the guest
          will never see it" on a screen that auto-advances in under a minute). Now a
          legible chip over the page's own dark backing (matches the before/after
          labels), turning bold red in the last 10s. */}
      {showCountdown && (stage === 'intro' || stage === 'select' || stage === 'review') ? (
        <div className="pointer-events-none absolute right-4 top-4 z-20 rounded-lg bg-black/45 px-3 py-1.5 shadow-sm backdrop-blur-sm">
          <span
            className={`font-mono text-2xl font-bold tabular-nums ${
              remainingTime <= 10 ? 'animate-pulse text-red-400' : 'text-white'
            }`}
          >
            {Math.floor(remainingTime / 60)}:{String(remainingTime % 60).padStart(2, '0')}
          </span>
        </div>
      ) : null}

      {stage === 'intro' ? (
        introPanel
      ) : stage === 'processing' ? (
        processingPanel
      ) : stage === 'review' ? (
        reviewPanel
      ) : (
        <div className={`flex min-h-0 flex-1 flex-col ${applyStrip ? 'p-4 sm:p-6' : ''} ${applyStrip || stackPreviewFirst ? '' : 'sm:flex-row'}`}>
          {/* Strip target: no photo to choose — just the whole strip + Apply/Skip (no grid). */}
          {applyStrip ? (
            previewPanel
          ) : stackPreviewFirst ? (
            <>
              {previewPanel}
              {gridSection}
            </>
          ) : (
            <>
              {gridSection}
              {previewPanel}
            </>
          )}
        </div>
      )}

      <PostSessionAiPaymentModal
        isVisible={showAiPayment}
        packages={aiPackages}
        themePrimary={themePrimary}
        title={introTitle}
        description={introDescription}
        beforeUrl={selectedBeforeUrl}
        timeout={aiTimeoutSec > 0 ? aiTimeoutSec : 60}
        onPaid={handleAiPaid}
        onSkip={onSkip}
      />
    </BackgroundContainer>
  )
}

export default PostSessionAIPage
